// Mock API service for Bulk Data Operations (CSV Import/Export)
// This provides a consistent interface for Salesforce data migration

export type Application = 'cybersweep' | 'credit-management' | 'credit-block' | 'regal-main';
export type JobStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface ImportMapping {
  csvColumn: string;
  fieldName: string;
  fieldLabel: string;
  required: boolean;
  transform?: 'uppercase' | 'lowercase' | 'trim' | 'capitalize';
}

export interface CsvPreview {
  headers: string[];
  preview: Record<string, any>[];
  rowCount: number;
  suggestedMappings: ImportMapping[];
}

export interface ImportJob {
  id: string;
  fileName: string;
  application: Application;
  entityType: string;
  status: JobStatus;
  totalRows: number;
  successRows: number;
  failedRows: number;
  startedAt: string;
  completedAt?: string;
  mappings: ImportMapping[];
  errors?: ImportError[];
}

export interface ExportJob {
  id: string;
  fileName: string;
  application: Application;
  entityType: string;
  status: JobStatus;
  totalRows: number;
  columns: string[];
  startedAt: string;
  completedAt?: string;
  downloadUrl?: string;
}

export interface ImportError {
  row: number;
  field: string;
  value: any;
  error: string;
  severity: 'error' | 'warning';
}

export interface ValidationResult {
  isValid: boolean;
  errors: ImportError[];
  warnings: ImportError[];
  summary: {
    totalRows: number;
    validRows: number;
    errorRows: number;
    warningRows: number;
  };
}

export interface BulkOperationStats {
  totalImports: number;
  totalExports: number;
  recentJobs: number;
  failedJobs: number;
  avgProcessingTime: number;
}

// Mock data
const mockImportJobs: ImportJob[] = [
  {
    id: 'import-001',
    fileName: 'cybersweep_clients_2025.csv',
    application: 'cybersweep',
    entityType: 'client',
    status: 'completed',
    totalRows: 150,
    successRows: 148,
    failedRows: 2,
    startedAt: '2025-01-15T10:30:00Z',
    completedAt: '2025-01-15T10:32:00Z',
    mappings: [
      { csvColumn: 'Client Name', fieldName: 'clientName__c', fieldLabel: 'Client Name', required: true },
      { csvColumn: 'Email', fieldName: 'email__c', fieldLabel: 'Email', required: true },
      { csvColumn: 'Phone', fieldName: 'phone__c', fieldLabel: 'Phone', required: false }
    ],
    errors: [
      { row: 45, field: 'email__c', value: 'invalid-email', error: 'Invalid email format', severity: 'error' },
      { row: 102, field: 'phone__c', value: '', error: 'Phone number missing', severity: 'warning' }
    ]
  },
  {
    id: 'import-002',
    fileName: 'credit_cases_batch_1.csv',
    application: 'credit-management',
    entityType: 'case',
    status: 'processing',
    totalRows: 500,
    successRows: 320,
    failedRows: 0,
    startedAt: '2025-01-15T14:20:00Z',
    mappings: [
      { csvColumn: 'Case ID', fieldName: 'caseId__c', fieldLabel: 'Case ID', required: true },
      { csvColumn: 'Status', fieldName: 'status__c', fieldLabel: 'Status', required: true },
      { csvColumn: 'Amount', fieldName: 'amount__c', fieldLabel: 'Amount', required: false }
    ]
  },
  {
    id: 'import-003',
    fileName: 'contacts_salesforce_migration.csv',
    application: 'regal-main',
    entityType: 'contact',
    status: 'failed',
    totalRows: 75,
    successRows: 0,
    failedRows: 75,
    startedAt: '2025-01-14T09:15:00Z',
    completedAt: '2025-01-14T09:16:00Z',
    mappings: [
      { csvColumn: 'First Name', fieldName: 'firstName__c', fieldLabel: 'First Name', required: true },
      { csvColumn: 'Last Name', fieldName: 'lastName__c', fieldLabel: 'Last Name', required: true }
    ],
    errors: [
      { row: 1, field: 'general', value: null, error: 'Database connection failed', severity: 'error' }
    ]
  }
];

const mockExportJobs: ExportJob[] = [
  {
    id: 'export-001',
    fileName: 'clients_export_2025-01-15.csv',
    application: 'cybersweep',
    entityType: 'client',
    status: 'completed',
    totalRows: 234,
    columns: ['clientName__c', 'email__c', 'phone__c', 'address__c', 'status__c'],
    startedAt: '2025-01-15T11:00:00Z',
    completedAt: '2025-01-15T11:01:30Z',
    downloadUrl: '/api/exports/export-001/download'
  },
  {
    id: 'export-002',
    fileName: 'cases_export_2025-01-15.csv',
    application: 'credit-management',
    entityType: 'case',
    status: 'processing',
    totalRows: 1000,
    columns: ['caseId__c', 'clientId__c', 'status__c', 'amount__c', 'createdDate__c'],
    startedAt: '2025-01-15T14:45:00Z'
  }
];

const mockStats: BulkOperationStats = {
  totalImports: 127,
  totalExports: 84,
  recentJobs: 15,
  failedJobs: 3,
  avgProcessingTime: 45
};

// Helper function to generate suggested mappings
const generateSuggestedMappings = (headers: string[]): ImportMapping[] => {
  return headers.map(header => {
    const normalized = header.toLowerCase().replace(/\s+/g, '_');
    const fieldName = `${normalized}__c`;

    // Common required fields
    const requiredFields = ['name', 'email', 'id', 'client_name', 'case_id'];
    const isRequired = requiredFields.some(field => normalized.includes(field));

    return {
      csvColumn: header,
      fieldName,
      fieldLabel: header,
      required: isRequired
    };
  });
};

// API Functions

/**
 * Upload and preview CSV file
 */
export const uploadCsvFile = async (
  file: File,
  application: Application,
  entityType: string
): Promise<{ data: CsvPreview; _usingMockData?: boolean }> => {
  console.log(`📤 [Mock API] Uploading CSV file: ${file.name}`, { application, entityType });

  await new Promise(resolve => setTimeout(resolve, 1000));

  // Simulate CSV parsing
  const mockHeaders = [
    'Client Name',
    'Email Address',
    'Phone Number',
    'Status',
    'Created Date',
    'Last Activity'
  ];

  const mockPreview = [
    {
      'Client Name': 'Acme Corporation',
      'Email Address': 'contact@acme.com',
      'Phone Number': '(555) 123-4567',
      'Status': 'Active',
      'Created Date': '2024-01-15',
      'Last Activity': '2025-01-10'
    },
    {
      'Client Name': 'TechStart Inc',
      'Email Address': 'info@techstart.com',
      'Phone Number': '(555) 987-6543',
      'Status': 'Active',
      'Created Date': '2024-02-20',
      'Last Activity': '2025-01-12'
    },
    {
      'Client Name': 'Global Enterprises',
      'Email Address': 'admin@global-ent.com',
      'Phone Number': '(555) 456-7890',
      'Status': 'Inactive',
      'Created Date': '2024-03-05',
      'Last Activity': '2024-12-28'
    }
  ];

  const suggestedMappings = generateSuggestedMappings(mockHeaders);

  return {
    data: {
      headers: mockHeaders,
      preview: mockPreview,
      rowCount: 150,
      suggestedMappings
    },
    _usingMockData: true
  };
};

/**
 * Start an import job
 */
export const startImportJob = async (
  file: File,
  application: Application,
  entityType: string,
  mappings: ImportMapping[]
): Promise<{ data: ImportJob; message: string; _usingMockData?: boolean }> => {
  console.log(`🚀 [Mock API] Starting import job for ${file.name}`, {
    application,
    entityType,
    mappingsCount: mappings.length
  });

  await new Promise(resolve => setTimeout(resolve, 1500));

  const newJob: ImportJob = {
    id: `import-${Date.now()}`,
    fileName: file.name,
    application,
    entityType,
    status: 'processing',
    totalRows: 150,
    successRows: 0,
    failedRows: 0,
    startedAt: new Date().toISOString(),
    mappings
  };

  mockImportJobs.unshift(newJob);

  // Simulate job completion after 5 seconds
  setTimeout(() => {
    newJob.status = 'completed';
    newJob.successRows = 148;
    newJob.failedRows = 2;
    newJob.completedAt = new Date().toISOString();
  }, 5000);

  return {
    data: newJob,
    message: 'Import job started successfully',
    _usingMockData: true
  };
};

/**
 * Fetch import jobs
 */
export const fetchImportJobs = async (filters?: {
  application?: Application;
  status?: JobStatus;
  entityType?: string;
}): Promise<{ data: ImportJob[]; _usingMockData?: boolean }> => {
  console.log('📋 [Mock API] Fetching import jobs', filters);

  await new Promise(resolve => setTimeout(resolve, 500));

  let filteredJobs = [...mockImportJobs];

  if (filters?.application) {
    filteredJobs = filteredJobs.filter(job => job.application === filters.application);
  }

  if (filters?.status) {
    filteredJobs = filteredJobs.filter(job => job.status === filters.status);
  }

  if (filters?.entityType) {
    filteredJobs = filteredJobs.filter(job => job.entityType === filters.entityType);
  }

  return {
    data: filteredJobs,
    _usingMockData: true
  };
};

/**
 * Fetch a single import job by ID
 */
export const fetchImportJob = async (
  jobId: string
): Promise<{ data: ImportJob; _usingMockData?: boolean }> => {
  console.log(`📄 [Mock API] Fetching import job: ${jobId}`);

  await new Promise(resolve => setTimeout(resolve, 300));

  const job = mockImportJobs.find(j => j.id === jobId);

  if (!job) {
    throw new Error(`Import job ${jobId} not found`);
  }

  return {
    data: job,
    _usingMockData: true
  };
};

/**
 * Start an export job
 */
export const startExportJob = async (
  application: Application,
  entityType: string,
  columns: string[]
): Promise<{ data: ExportJob; message: string; _usingMockData?: boolean }> => {
  console.log('🚀 [Mock API] Starting export job', {
    application,
    entityType,
    columnsCount: columns.length
  });

  await new Promise(resolve => setTimeout(resolve, 1000));

  const timestamp = new Date().toISOString().split('T')[0];
  const newJob: ExportJob = {
    id: `export-${Date.now()}`,
    fileName: `${entityType}_export_${timestamp}.csv`,
    application,
    entityType,
    status: 'processing',
    totalRows: 0,
    columns,
    startedAt: new Date().toISOString()
  };

  mockExportJobs.unshift(newJob);

  // Simulate job completion after 3 seconds
  setTimeout(() => {
    newJob.status = 'completed';
    newJob.totalRows = 234;
    newJob.completedAt = new Date().toISOString();
    newJob.downloadUrl = `/api/exports/${newJob.id}/download`;
  }, 3000);

  return {
    data: newJob,
    message: 'Export job started successfully',
    _usingMockData: true
  };
};

/**
 * Fetch export jobs
 */
export const fetchExportJobs = async (filters?: {
  application?: Application;
  status?: JobStatus;
  entityType?: string;
}): Promise<{ data: ExportJob[]; _usingMockData?: boolean }> => {
  console.log('📋 [Mock API] Fetching export jobs', filters);

  await new Promise(resolve => setTimeout(resolve, 500));

  let filteredJobs = [...mockExportJobs];

  if (filters?.application) {
    filteredJobs = filteredJobs.filter(job => job.application === filters.application);
  }

  if (filters?.status) {
    filteredJobs = filteredJobs.filter(job => job.status === filters.status);
  }

  if (filters?.entityType) {
    filteredJobs = filteredJobs.filter(job => job.entityType === filters.entityType);
  }

  return {
    data: filteredJobs,
    _usingMockData: true
  };
};

/**
 * Download export file
 */
export const downloadExportFile = (jobId: string): void => {
  console.log(`⬇️ [Mock API] Downloading export file for job: ${jobId}`);

  const job = mockExportJobs.find(j => j.id === jobId);

  if (!job || !job.downloadUrl) {
    alert('Export file not ready or job not found');
    return;
  }

  // Simulate file download
  const csvContent = `Client Name,Email,Status\nAcme Corp,contact@acme.com,Active\nTechStart,info@tech.com,Inactive`;
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = job.fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Validate import data before importing
 */
export const validateImportData = async (
  file: File,
  application: Application,
  entityType: string,
  mappings: ImportMapping[]
): Promise<{ data: ValidationResult; _usingMockData?: boolean }> => {
  console.log(`✅ [Mock API] Validating import data for ${file.name}`, {
    application,
    entityType,
    mappingsCount: mappings.length
  });

  await new Promise(resolve => setTimeout(resolve, 1500));

  // Simulate validation results
  const errors: ImportError[] = [
    {
      row: 45,
      field: 'email__c',
      value: 'invalid-email',
      error: 'Invalid email format',
      severity: 'error'
    },
    {
      row: 78,
      field: 'clientName__c',
      value: '',
      error: 'Required field is empty',
      severity: 'error'
    }
  ];

  const warnings: ImportError[] = [
    {
      row: 102,
      field: 'phone__c',
      value: '',
      error: 'Phone number missing (optional field)',
      severity: 'warning'
    },
    {
      row: 130,
      field: 'address__c',
      value: 'N/A',
      error: 'Address field contains placeholder value',
      severity: 'warning'
    }
  ];

  return {
    data: {
      isValid: errors.length === 0,
      errors,
      warnings,
      summary: {
        totalRows: 150,
        validRows: 146,
        errorRows: 2,
        warningRows: 2
      }
    },
    _usingMockData: true
  };
};

/**
 * Fetch bulk operation statistics
 */
export const fetchBulkOperationStats = async (): Promise<{
  data: BulkOperationStats;
  _usingMockData?: boolean;
}> => {
  console.log('📊 [Mock API] Fetching bulk operation statistics');

  await new Promise(resolve => setTimeout(resolve, 400));

  return {
    data: mockStats,
    _usingMockData: true
  };
};
